import { useState, useEffect } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { IconX } from "@tabler/icons-react";
import BaseInput from "../components/base_input";
import PriceInput from "../components/price_input";
import EditItemImages from "./edit_item_images";
import { IconChevronDown } from "@tabler/icons-react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { put, post } from "../api";
import useNotify from "../notification/use_notification";

function EditItem({ item, isOpen, close, groups }) {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [state, setState] = useState(null);
  useEffect(() => setState(item), [item]);
  const handleChange = (e) =>
    setState({ ...state, [e.target.name]: e.target.value });
  const mutation = useMutation({
    mutationFn: async (item) => {
      if (item.item_id) {
        await put(`/items/${item.item_id}`, item);
      } else {
        await post("/items", item);
      }
    },
    onError: (error) => notify.error(error),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["shop"] });
      close();
    },
  });
  const handleSave = () => {
    if (!state.item_name) {
      notify.warn("Item must have name");
      return;
    }
    if (!state.item_description) {
      notify.warn("Item must have description");
      return;
    }
    const item_price = parseInt(state.item_price);
    if (!item_price) {
      notify.warn("Item must have valid price");
      return;
    }
    const item_group_id = parseInt(state.item_group_id);
    if (!item_group_id || item_group_id < 1) {
      notify.warn("Item must have valid group");
      return;
    }
    mutation.mutate({
      ...state,
      item_price: item_price,
      item_group_id: item_group_id,
      item_type: -1,
      order_priority: 0,
    });
  };
  return (
    <DialogWrapper isOpen={isOpen} close={close}>
      {state && (
        <div className="flex flex-col gap-2">
          <BaseInput
            id="name"
            value={state.item_name}
            onChange={handleChange}
            name="item_name"
            label="Name"
          />

          <BaseInput
            id="description"
            value={state.item_description}
            onChange={handleChange}
            name="item_description"
            label="Description"
          />
          <PriceInput
            value={state.item_price}
            onChange={handleChange}
            name="item_price"
            label="Price"
          />
          <div>
            <label
              htmlFor="group"
              className="block text-sm/6 font-medium text-gray-900"
            >
              Group
            </label>
            <div className="mt-2 grid grid-cols-1">
              <select
                id="group"
                name="item_group_id"
                value={state.item_group_id}
                onChange={handleChange}
                className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
              >
                <option value={-1} className="text-slate-300" disabled hidden>
                  Select Group
                </option>
                {groups.map((group) => (
                  <option key={group.group_key} value={group.item_group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
              <IconChevronDown
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
              />
            </div>
          </div>
          <div className="relative flex gap-3 py-4">
            <div className="min-w-0 flex-1 text-sm/6">
              <label
                htmlFor="is_enabled"
                className="select-none font-medium text-gray-900"
              >
                Is Enabled
              </label>
            </div>
            <div className="flex h-6 shrink-0 items-center">
              <div className="group grid size-4 grid-cols-1">
                <input
                  id="is_enabled"
                  name="enable_status"
                  type="checkbox"
                  checked={state.enable_status === 0}
                  onChange={(e) =>
                    setState({
                      ...state,
                      enable_status: state.enable_status === 0 ? 1 : 0,
                    })
                  }
                  className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                />
                <svg
                  fill="none"
                  viewBox="0 0 14 14"
                  className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                >
                  <path
                    d="M3 8L6 11L11 3.5"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-0 group-has-[:checked]:opacity-100"
                  />
                  <path
                    d="M3 7H11"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-0 group-has-[:indeterminate]:opacity-100"
                  />
                </svg>
              </div>
            </div>
          </div>
          <EditItemImages item={state} onChange={handleChange} />
          <div className="flex gap-2 justify-end mt-6">
            <button
              onClick={close}
              type="button"
              className="rounded-md bg-slate-200 px-2.5 py-1.5 text-sm font-semibold text-black shadow-sm hover:bg-slate-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600"
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              type="button"
              className="rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </DialogWrapper>
  );
}

function DialogWrapper({ isOpen, close, children }) {
  return (
    <Dialog open={isOpen} onClose={close} className="relative z-40">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                <div className="px-4 sm:px-6">
                  <div className="flex items-start justify-between">
                    <DialogTitle className="text-base font-semibold text-gray-900">
                      Edit Item
                    </DialogTitle>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => close()}
                        className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <IconX aria-hidden="true" className="size-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                  {children}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
export default EditItem;
